import { MARKETING_HTML_LANG, hreflangPair, type MarketingLocale } from '../lib/marketingLocale'
import { MARKETING_DOCUMENT_PATHS, ogImagePath } from '../lib/marketingPages'
import { useSeo } from '../lib/seo'
import { TERMS } from '../content/legalCopy'
import { MarketingShell, MarketingTitle } from '../components/MarketingShell'
import { TermsBodyEn } from './legal/TermsBodyEn'
import { TermsBodyDe } from './legal/TermsBodyDe'

const PATHS = MARKETING_DOCUMENT_PATHS.terms

/**
 * /terms (EN) and /de/terms (DE).
 *
 * A pair since 2026-09-09, on the same mechanism every other marketing document
 * uses — the language is the ROUTE's, never the visitor's UI locale, so the
 * language switch in the shell now renders here too. See `content/legalCopy.ts`
 * for why "English only, deliberately" reversed, and `pages/legal/` for the two
 * bodies: the English one is the binding text, the German one a convenience
 * translation whose clause 16 says so.
 *
 * Both bodies are imported statically rather than lazily. They land in the same
 * route chunk, which costs a German reader the English text and vice versa —
 * about 20 KB of prose, once, on a page nobody visits twice. A second lazy
 * boundary inside an already-lazy route would buy that back at the price of a
 * loading state in the middle of a contract.
 *
 * Re-chromed onto MarketingShell on 2026-08-19 (finding F8): before that it and
 * /privacy were the last public pages rendering as app-styled documents, with a
 * colour scheme that followed the app's light/dark class, so a visitor arriving
 * from the dark marketing footer landed on a white page with no way back.
 */
export function TermsPage({ locale }: { locale: MarketingLocale }) {
  const copy = TERMS[locale]

  useSeo({
    title: copy.meta.title,
    description: copy.meta.description,
    canonicalPath: PATHS[locale],
    lang: MARKETING_HTML_LANG[locale],
    alternates: hreflangPair(PATHS),
    ogImage: ogImagePath(PATHS[locale]),
  })

  return (
    <MarketingShell locale={locale}>
      <MarketingTitle>{copy.h1}</MarketingTitle>
      <p className="text-sm mb-4" style={{ color: '#6b6580' }}>{copy.updated}</p>
      {locale === 'de' ? <TermsBodyDe /> : <TermsBodyEn />}
    </MarketingShell>
  )
}

export default TermsPage
